import { useState, useMemo } from 'react';
import {
  Eye,
  Hand,
  Brain,
  Wrench as RobustIcon,
  Filter,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  X,
} from 'lucide-react';
import type { AccessibilityIssue, PageResult } from '../../types';
import { GradeBadge, ScoreGauge, getGradeFromScore } from '../ui/GradeBadge';
import { IssueCard, IssueCardSkeleton } from '../ui/IssueCard';
import type { IssueCardData, WcagPrinciple, ToolSource } from '../ui/IssueCard';

interface AccessibilityDetailsProps {
  pageResults: PageResult[];
  accessibilityScore?: number;
  loading?: boolean;
  onPageSelect?: (pageUrl: string | null) => void;
  selectedPageUrl?: string | null;
}

type SeverityFilter = 'all' | 'critical' | 'serious' | 'moderate' | 'minor';
type PrincipleFilter = 'all' | 'perceivable' | 'operable' | 'understandable' | 'robust';
type ToolFilter = 'all' | 'AXE' | 'LIGHTHOUSE' | 'PA11Y';

const PRINCIPLE_CONFIG: Record<
  Exclude<PrincipleFilter, 'all'>,
  { label: string; icon: typeof Eye; color: string }
> = {
  perceivable: { label: 'Perceivable', icon: Eye, color: 'text-blue-600' },
  operable: { label: 'Operable', icon: Hand, color: 'text-green-600' },
  understandable: { label: 'Understandable', icon: Brain, color: 'text-purple-600' },
  robust: { label: 'Robust', icon: RobustIcon, color: 'text-orange-600' },
};

// Map WCAG criteria to principles
function getPrincipleFromWcag(wcagCriteria: string[]): WcagPrinciple | undefined {
  if (!wcagCriteria || wcagCriteria.length === 0) return undefined;
  const firstCriterion = wcagCriteria[0];
  const principleNumber = firstCriterion.split('.')[0];
  switch (principleNumber) {
    case '1':
      return 'perceivable';
    case '2':
      return 'operable';
    case '3':
      return 'understandable';
    case '4':
      return 'robust';
    default:
      return undefined;
  }
}

function convertToIssueCardData(issue: AccessibilityIssue, pageUrl?: string): IssueCardData {
  return {
    ruleId: issue.ruleId,
    title: issue.description.split('.')[0] || issue.ruleId,
    description: issue.description,
    severity: issue.impact,
    wcagCriteria: issue.wcagCriteria,
    wcagLevel: issue.wcagLevel,
    wcagPrinciple: getPrincipleFromWcag(issue.wcagCriteria),
    toolSource: issue.toolSource as ToolSource,
    helpUrl: issue.helpUrl,
    pageUrl,
    affectedElements: issue.nodes?.map((node) => ({
      selector: node.target?.join(' > ') || '',
      html: node.html,
      failureSummary: node.failureSummary,
    })),
    count: issue.nodes?.length || 1,
  };
}

function SkeletonScore() {
  return (
    <div className="animate-pulse flex flex-col items-center">
      <div className="w-[120px] h-[120px] rounded-full bg-gray-light" />
      <div className="h-4 bg-gray-light rounded w-24 mt-3" />
    </div>
  );
}

function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-body-sm text-gray-medium">{label}:</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as T)}
          className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
      </div>
    </div>
  );
}

export function AccessibilityDetails({
  pageResults,
  accessibilityScore,
  loading = false,
  selectedPageUrl,
}: AccessibilityDetailsProps) {
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [principleFilter, setPrincipleFilter] = useState<PrincipleFilter>('all');
  const [toolFilter, setToolFilter] = useState<ToolFilter>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Aggregate all accessibility issues across pages, grouped by ruleId
  const allIssues = useMemo(() => {
    const issueMap = new Map<string, IssueCardData>();

    for (const page of pageResults) {
      if (selectedPageUrl && page.pageUrl !== selectedPageUrl) continue;
      if (!page.accessibilityIssues) continue;

      for (const issue of page.accessibilityIssues) {
        const key = issue.ruleId;
        const existing = issueMap.get(key);

        if (existing) {
          const newElements =
            issue.nodes?.map((node) => ({
              selector: node.target?.join(' > ') || '',
              html: node.html,
              failureSummary: node.failureSummary,
            })) || [];
          existing.affectedElements = [...(existing.affectedElements || []), ...newElements];
          existing.count = (existing.count || 0) + (issue.nodes?.length || 1);
          if (!existing.affectedPages?.includes(page.pageUrl)) {
            existing.affectedPages = [...(existing.affectedPages || []), page.pageUrl];
          }
        } else {
          issueMap.set(key, {
            ...convertToIssueCardData(issue, page.pageUrl),
            affectedPages: [page.pageUrl],
          });
        }
      }
    }

    return Array.from(issueMap.values());
  }, [pageResults, selectedPageUrl]);

  // Apply filters
  const filteredIssues = useMemo(() => {
    return allIssues.filter((issue) => {
      if (severityFilter !== 'all' && issue.severity !== severityFilter) return false;
      if (principleFilter !== 'all' && issue.wcagPrinciple !== principleFilter) return false;
      if (toolFilter !== 'all' && issue.toolSource !== toolFilter) return false;
      return true;
    });
  }, [allIssues, severityFilter, principleFilter, toolFilter]);

  // Calculate issue counts by severity
  const issueCounts = useMemo(() => {
    return allIssues.reduce(
      (acc, issue) => {
        acc[issue.severity]++;
        acc.total++;
        return acc;
      },
      { critical: 0, serious: 0, moderate: 0, minor: 0, total: 0 }
    );
  }, [allIssues]);

  // Calculate issue counts by principle
  const principalCounts = useMemo(() => {
    return allIssues.reduce(
      (acc, issue) => {
        if (issue.wcagPrinciple) {
          acc[issue.wcagPrinciple]++;
        }
        return acc;
      },
      { perceivable: 0, operable: 0, understandable: 0, robust: 0 }
    );
  }, [allIssues]);

  // Calculate score for selected page if applicable
  const displayScore = useMemo(() => {
    if (selectedPageUrl) {
      const page = pageResults.find((p) => p.pageUrl === selectedPageUrl);
      return page?.score;
    }
    return accessibilityScore;
  }, [accessibilityScore, selectedPageUrl, pageResults]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="card">
          <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
            <Eye className="text-coral" size={24} />
            Accessibility Score
          </h3>
          <div className="flex justify-center">
            <SkeletonScore />
          </div>
        </div>
        <div>
          <h3 className="text-heading-3 font-semibold mb-4">Accessibility Issues</h3>
          <IssueCardSkeleton count={5} />
        </div>
      </div>
    );
  }

  const hasScore = displayScore !== undefined;
  const grade = hasScore ? getGradeFromScore(displayScore) : undefined;

  return (
    <div className="space-y-8">
      {/* Score Section */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
          <Eye className="text-coral" size={24} />
          Accessibility Score
          {selectedPageUrl && (
            <span className="text-body-sm font-normal text-gray-medium ml-2">
              (Filtered by page)
            </span>
          )}
        </h3>

        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Score Gauge */}
          <div className="flex flex-col items-center">
            {hasScore ? (
              <>
                <ScoreGauge score={displayScore} showLabel={false} />
                <div className="flex items-center gap-2 mt-3">
                  <GradeBadge grade={grade!} size="sm" />
                  <span className="text-body-sm text-gray-medium">
                    {grade === 'A'
                      ? 'Excellent'
                      : grade === 'B'
                        ? 'Good'
                        : grade === 'C'
                          ? 'Fair'
                          : grade === 'D'
                            ? 'Poor'
                            : 'Needs Work'}
                  </span>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="w-[120px] h-[120px] rounded-full border-8 border-gray-light flex items-center justify-center">
                  <span className="text-heading-3 text-gray-medium">N/A</span>
                </div>
                <p className="text-body-sm text-gray-medium mt-3">No score available</p>
              </div>
            )}
          </div>

          {/* Issue Summary */}
          <div className="flex-1 w-full">
            <h4 className="text-body font-semibold mb-3 text-gray-medium">Issues by Severity</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-severity-critical" />
                  <span className="text-heading-3 font-bold text-red-800">
                    {issueCounts.critical}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Critical</span>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-severity-serious" />
                  <span className="text-heading-3 font-bold text-orange-800">
                    {issueCounts.serious}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Serious</span>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-severity-moderate" />
                  <span className="text-heading-3 font-bold text-yellow-800">
                    {issueCounts.moderate}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Moderate</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-severity-minor" />
                  <span className="text-heading-3 font-bold text-gray-700">
                    {issueCounts.minor}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Minor</span>
              </div>
            </div>

            {/* POUR Breakdown */}
            <h4 className="text-body font-semibold mb-3 text-gray-medium">
              Issues by WCAG Principle
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(
                Object.entries(PRINCIPLE_CONFIG) as [
                  Exclude<PrincipleFilter, 'all'>,
                  (typeof PRINCIPLE_CONFIG)['perceivable'],
                ][]
              ).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Icon size={16} className={config.color} />
                    <span className="text-body-sm font-medium">{principalCounts[key]}</span>
                    <span className="text-caption text-gray-medium">{config.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-coral" />
            <span className="text-body font-semibold">Filters</span>
            {(severityFilter !== 'all' || principleFilter !== 'all' || toolFilter !== 'all') && (
              <span className="px-2 py-0.5 bg-coral text-white text-caption rounded-full">
                Active
              </span>
            )}
          </div>
          <ChevronDown
            size={18}
            className={`text-gray-medium transition-transform ${showFilters ? 'rotate-180' : ''}`}
          />
        </button>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-light flex flex-wrap gap-4">
            <FilterDropdown
              label="Severity"
              value={severityFilter}
              options={[
                { value: 'all', label: 'All Severities' },
                { value: 'critical', label: 'Critical' },
                { value: 'serious', label: 'Serious' },
                { value: 'moderate', label: 'Moderate' },
                { value: 'minor', label: 'Minor' },
              ]}
              onChange={setSeverityFilter}
            />
            <FilterDropdown
              label="WCAG Principle"
              value={principleFilter}
              options={[
                { value: 'all', label: 'All Principles' },
                { value: 'perceivable', label: 'Perceivable' },
                { value: 'operable', label: 'Operable' },
                { value: 'understandable', label: 'Understandable' },
                { value: 'robust', label: 'Robust' },
              ]}
              onChange={setPrincipleFilter}
            />
            <FilterDropdown
              label="Detection Tool"
              value={toolFilter}
              options={[
                { value: 'all', label: 'All Tools' },
                { value: 'AXE', label: 'axe-core' },
                { value: 'LIGHTHOUSE', label: 'Lighthouse' },
                { value: 'PA11Y', label: 'Pa11y' },
              ]}
              onChange={setToolFilter}
            />

            {(severityFilter !== 'all' || principleFilter !== 'all' || toolFilter !== 'all') && (
              <button
                onClick={() => {
                  setSeverityFilter('all');
                  setPrincipleFilter('all');
                  setToolFilter('all');
                }}
                className="flex items-center gap-1 text-body-sm text-coral hover:text-coral/80"
              >
                <X size={14} />
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Issues List */}
      <div>
        <h3 className="text-heading-3 font-semibold mb-4 flex items-center gap-2">
          <AlertTriangle className="text-coral" size={24} />
          Accessibility Issues ({filteredIssues.length})
        </h3>

        {filteredIssues.length > 0 ? (
          <div className="space-y-4">
            {filteredIssues.map((issue) => (
              <IssueCard key={issue.ruleId} issue={issue} showPageUrl={!selectedPageUrl} />
            ))}
          </div>
        ) : issueCounts.total === 0 ? (
          <div className="card text-center py-12">
            <CheckCircle size={48} className="mx-auto text-status-success opacity-50 mb-4" />
            <h3 className="text-heading-3 font-semibold text-dark-text mb-2">
              No Accessibility Issues Found
            </h3>
            <p className="text-body text-gray-medium">
              Great work! No accessibility issues were detected on the scanned pages.
            </p>
          </div>
        ) : (
          <div className="card text-center py-12">
            <Filter size={48} className="mx-auto text-gray-medium opacity-50 mb-4" />
            <h3 className="text-heading-3 font-semibold text-dark-text mb-2">
              No Issues Match Filters
            </h3>
            <p className="text-body text-gray-medium">
              Try adjusting your filters to see more issues.
            </p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-body-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-severity-critical" />
          <span className="text-gray-medium">Critical (-25 points)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-severity-serious" />
          <span className="text-gray-medium">Serious (-15 points)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-severity-moderate" />
          <span className="text-gray-medium">Moderate (-7 points)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-severity-minor" />
          <span className="text-gray-medium">Minor (-3 points)</span>
        </div>
      </div>
    </div>
  );
}
